import { useEffect, useState } from 'react';

interface UseH5pAssetsOptions {
  integration?: Record<string, unknown>;
}

export function useH5pAssets(
  styles: string[],
  scripts: string[],
  options: UseH5pAssetsOptions = {},
) {
  const { integration } = options;
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stylesKey = styles.join('|');
  const scriptsKey = scripts.join('|');

  useEffect(() => {
    setReady(false);
    setError(null);

    if (styles.length === 0 && scripts.length === 0) {
      setReady(true);
      return;
    }

    if (integration) {
      window.H5PIntegration = integration;
    }

    window.H5P = window.H5P || {};
    window.H5P.preventInit = true;

    const createdLinks: HTMLLinkElement[] = [];
    styles.forEach((href) => {
      const existing = document.querySelector(`link[rel="stylesheet"][href="${href}"]`);
      if (existing) return;

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
      createdLinks.push(link);
    });

    let cancelled = false;

    const loadScripts = async () => {
      try {
        document.querySelectorAll('script[src*="/h5p-core/"], script[src*="/h5p-editor/"]').forEach((node) => {
          node.remove();
        });

        for (const src of scripts) {
          if (cancelled) return;

          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = false;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
            document.body.appendChild(script);
          });
        }

        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message);
        }
      }
    };

    void loadScripts();

    return () => {
      cancelled = true;
      createdLinks.forEach((link) => link.remove());
    };
  }, [stylesKey, scriptsKey, integration]);

  return { ready, error };
}

declare global {
  interface Window {
    H5PIntegration?: Record<string, unknown>;
    H5P?: {
      jQuery?: unknown;
      preventInit?: boolean;
      init?: (target: HTMLElement) => void;
      instances?: { contentId?: string | number; on?: (event: string, handler: (event: unknown) => void) => void; off?: (event: string, handler: (event: unknown) => void) => void }[];
      on?: (instance: { on?: (event: string, handler: (event: unknown) => void) => void }, event: string, handler: (event: unknown) => void) => void;
      externalDispatcher?: {
        on: (event: string, cb: (ev: unknown) => void) => void;
        off?: (event: string, cb: (ev: unknown) => void) => void;
      };
      trigger?: (instance: unknown, event: string) => void;
    };
    H5PEditor?: Record<string, unknown> & {
      Editor?: import('./types').H5pEditorCtor;
      $?: unknown;
      basePath?: string;
      fileIcon?: unknown;
      ajaxPath?: string;
      filesPath?: string;
      apiVersion?: unknown;
      contentLanguage?: string;
      copyrightSemantics?: unknown;
      metadataSemantics?: unknown;
      assets?: unknown;
      baseUrl?: string;
      enableContentHub?: boolean;
      getAjaxUrl?: (action: string, parameters?: Record<string, unknown>) => string;
    };
  }
}
